import React, { memo, useId, useMemo } from 'react';
import cn from 'classnames';
import styles from './BannerStrip.module.scss';
import LinkButton from '_components/UI/LinkButton';
import IconWrapper from '@components/ui/Icons';
import iconMap from '@components/ui/Icons/iconMap';
import { phosphorIcons } from './phosphorIconList';
import {
  trackBannerStripClick,
  type BannerStripInteractionLocation,
} from '_utils/GA4/trackBannerStripEvent';
import { BANNER_STRIP_DEFAULT_ICONS } from './defaultIcons';

const ICON_SIZE = 40;

type PhosphorWeight =
  | 'thin'
  | 'light'
  | 'regular'
  | 'fill'
  | 'duotone'
  | 'bold';

const PHOSPHOR_ICON_NAME = /^(.+)-(thin|light|regular|fill|duotone|bold)$/;

const toPhosphorExportName = (name: string) => {
  const pascal = name
    .split(/[\s_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  return pascal.endsWith('Icon') ? pascal : `${pascal}Icon`;
};

const getIconStyle = (variant: string) => {
  if (variant === 'membership') {
    return {
      fill: 'var(--neutrals-100)',
      color: 'var(--neutrals-100)',
    };
  }
  if (variant === 'light') {
    return {
      fill: 'var(--neutrals-1000)',
      color: 'var(--neutrals-1000)',
    };
  }
  return {};
};

// Only icons in phosphorIconList or iconMap are bundled. Importing anything
// else from @phosphor-icons/react at runtime pulls in the whole package.
const renderIcon = (iconName: string, variant: string) => {
  const phosphorMatch = iconName.match(PHOSPHOR_ICON_NAME);
  if (phosphorMatch) {
    const [, name, weight] = phosphorMatch;
    const PhosphorIcon =
      phosphorIcons[toPhosphorExportName(name) as keyof typeof phosphorIcons];
    if (PhosphorIcon) {
      return (
        <PhosphorIcon
          size={ICON_SIZE}
          weight={weight as PhosphorWeight}
          {...getIconStyle(variant)}
          aria-hidden
        />
      );
    }
  }
  if (iconName in iconMap) {
    return (
      <IconWrapper
        icon={iconName}
        width={ICON_SIZE}
        height={ICON_SIZE}
        {...getIconStyle(variant)}
      />
    );
  }
  return null;
};

export type BannerStripProps = {
  className?: string;
  /**
   * Name of the icon to display
   */
  iconName?: string;
  /**
   * Banner title (main text)
   */
  title?: React.ReactNode;
  /**
   * Banner subtitle (secondary text)
   */
  subtitle?: React.ReactNode;
  /**
   * CTA text
   */
  ctaText?: string;
  /**
   * CTA URL (optional)
   */
  ctaUrl?: string;
  /**
   * Optional: style variant
   */
  variant?: 'membership' | 'light';
  /**
   * Where the strip sits on the page, sent with click tracking
   */
  interactionLocation?: BannerStripInteractionLocation;
};

const BannerStrip: React.FC<BannerStripProps> = memo(
  ({
    className = '',
    iconName,
    title,
    subtitle,
    ctaText,
    ctaUrl,
    variant = 'membership',
    interactionLocation = 'body',
  }) => {
    const icon = useMemo(() => {
      if (!iconName) return null;
      const resolved = renderIcon(iconName, variant);
      if (resolved) return resolved;
      console.error(
        `BannerStrip: Icon "${iconName}" is not in phosphorIconList or iconMap; showing the ${variant} default.`
      );
      return renderIcon(BANNER_STRIP_DEFAULT_ICONS[variant], variant);
    }, [iconName, variant]);

    const titleId = useId();
    const subtitleId = useId();
    let labelId: string | undefined;
    if (title) labelId = titleId;
    else if (subtitle) labelId = subtitleId;

    const handleCTAClick = () => {
      if (ctaUrl && ctaText) {
        trackBannerStripClick({
          data: { linkUrl: ctaUrl, linkText: ctaText, interactionLocation },
        });
      }
      if (ctaUrl) {
        window.location.href = ctaUrl;
      }
    };

    return (
      <div
        className={cn(styles.bannerStrip, styles[variant], className)}
        role="region"
        aria-labelledby={labelId}
      >
        {icon}
        <div className={styles.textContainer}>
          {title && (
            <div id={titleId} className={styles.title}>
              {title}
            </div>
          )}
          {subtitle && (
            <div id={subtitleId} className={styles.subtitle}>
              {subtitle}
            </div>
          )}
        </div>
        {ctaUrl && ctaText && (
          <div className={styles.ctaContainer}>
            <LinkButton
              href={ctaUrl}
              variant={variant === 'membership' ? 'white' : 'black'}
              onClick={() => handleCTAClick()}
            >
              {ctaText}
            </LinkButton>
          </div>
        )}
      </div>
    );
  }
);

BannerStrip.displayName = 'BannerStrip';

export default BannerStrip;
